type Listener = () => void;
import { StringUtils } from "@/utils/StringUtilts";
import { cloneDeep, uniqueId } from "es-toolkit/compat";
import { DraggingModule } from "./DraggingModule";
import { ResizingModule } from "./ResizingModule";
import { GRID_CONFIG } from "../config/grid-config";
export interface IGridItem {
    empty?: boolean;
    id: string;
    data: any;
    colSpan?: number;
    colStart: number;
}

interface ICoordinates {
    row: number;
    col: number;
    colSpan: number;
}

class Grid {
    dimensions: { rows: number; cols: number };
    grid: IGridItem[][];
    listeners = new Set<Listener>();
    cachedSnapshot: IGridItem[][] | null = null;
    autoRemoveEmptyRows: boolean;
    // * Modules
    draggingModule: DraggingModule;
    resizingModule: ResizingModule;
    constructor(grid: IGridItem[][], autoRemoveEmptyRows: boolean = GRID_CONFIG.AUTO_REMOVE_EMPTY_ROWS, maxCols: number = GRID_CONFIG.MAX_COLUMNS) {
        this.dimensions = { rows: grid.length ?? 0, cols: maxCols };
        this.grid = grid.length > 0 ? this.cleanDuplicateItems(grid) : [new Array(maxCols).fill(0).map((_, colIndex) => Grid.emptyItem(1, colIndex))];
        this.draggingModule = new DraggingModule(this);
        this.resizingModule = new ResizingModule(this);
        this.autoRemoveEmptyRows = autoRemoveEmptyRows;
    }

    get flatMapGrid() {
        const _flatMapGrid: Record<string, IGridItem> = {};
        this.grid.forEach((row) => {
            row.forEach((item) => {
                _flatMapGrid[item.id ?? ""] = item;
            });
        });
        return _flatMapGrid;
    }

    getSnapshot() {
        const _grid = this.autoRemoveEmptyRows ? this.removeEmptyRows(this.grid) : this.grid;
        if (this.cachedSnapshot === null) {
            this.cachedSnapshot = _grid.map((row, rowIndex) => row.map((item) => ({ ...item, row: rowIndex })));
        }
        return this.cachedSnapshot;
    }

    subscribe(listener: Listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    cleanDuplicateItems(grid: IGridItem[][]) {
        const _grid = cloneDeep(grid);
        return _grid.map((row) => {
            const uniqueItems = new Set(row.map((item) => item.id));
            return Array.from(uniqueItems).map((id) => row.find((item) => item.id === id)!);
        });
    }

    removeEmptyRows(grid: IGridItem[][]) {
        return grid.filter((row) => !row.every((item) => item.empty)).map((row, rowIndex) => row.map((item) => ({ ...item, row: rowIndex })));
    }

    setGrid(next: IGridItem[][]) {
        console.log("## SET GRID", next);
        this.grid = this.cleanDuplicateItems(next);
        this.cachedSnapshot = null; // Invalidate cache
        for (const l of this.listeners) l();
    }

    getRow(itemId: string): number {
        for (let rowIndex = 0; rowIndex < this.grid.length; rowIndex++) {
            for (let colIndex = 0; colIndex < this.grid[rowIndex].length; colIndex++) {
                if (this.grid[rowIndex][colIndex].id === itemId) {
                    return rowIndex;
                }
            }
        }
        return -1; // Item not found
    }

    addItemToRow(_gridItem: Omit<IGridItem, "colStart">, targetRow: number) {
        const grid = cloneDeep(this.grid);
        const virtualRow = Grid.virtualizeRow(grid[targetRow]);
        const { hasSpace, colStart } = this.getRowConsecutiveSpace(virtualRow, _gridItem.colSpan ?? 1);
        const gridItem: IGridItem = {
            id: _gridItem.id,
            data: _gridItem.data,
            colSpan: _gridItem.colSpan,
            colStart,
        };
        if (!hasSpace) {
            this.addRow(targetRow);
            this.placeItem({ ...gridItem, colStart: 0 }, { row: targetRow, col: 0, colSpan: gridItem.colSpan ?? 1 }, 0);
            return;
        }
        this.placeItem(gridItem, { row: targetRow, col: colStart, colSpan: gridItem.colSpan ?? 1 }, colStart);
    }

    addRow(aboveRow?: number) {
        const lastRow = this.grid.length - 1;
        if (aboveRow !== undefined) {
            // Create new empty row
            const newRow = new Array(this.dimensions.cols).fill(0).map((_, colIndex) => Grid.emptyItem(1, colIndex));

            const newGrid = [...this.grid.slice(0, aboveRow), newRow, ...this.grid.slice(aboveRow)];

            // Update dimensions
            this.dimensions.rows = newGrid.length;
            this.setGrid(newGrid);
            return aboveRow;
        } else {
            // Add row at the end
            const newRow = new Array(this.dimensions.cols).fill(0).map((_, colIndex) => Grid.emptyItem(1, colIndex));
            this.dimensions.rows = this.grid.length + 1;
            this.setGrid([...this.grid, newRow]);
            return lastRow + 1;
        }
    }

    placeItem(gridItem: IGridItem, coordinates: ICoordinates | null, colStart: number) {
        if (!coordinates) return;
        console.log("*** PLACING ITEM ***", { gridItem, coordinates });
        const _grid = cloneDeep(this.grid);
        const virtualRow = Grid.virtualizeRow(_grid[coordinates.row]);
        // debugger;
        for (let i = colStart; i < colStart + Math.max(gridItem.colSpan ?? 1, coordinates.colSpan ?? 1); i++) {
            virtualRow[i] = { ...gridItem, colStart };
            if (i >= colStart + (gridItem.colSpan ?? 1)) {
                virtualRow[i] = Grid.emptyItem(1, i);
            }
            console.log("*** VIRTUAL ITEM ***", virtualRow[i]);
        }
        console.log("*** PLACING ITEM ***", { gridItem, coordinates, virtualRow, _grid });
        _grid[coordinates.row] = Grid.devirtualizeRow(virtualRow);
        this.setGrid(_grid);
    }

    replaceItemWithEmptyItems(row: number, colSpan: number, colStart: number) {
        const _grid = cloneDeep(this.grid);
        console.log("### REPLACING ITEM WITH EMPTY ITEMS ###", { row, colSpan, colStart, _grid });
        for (let i = colStart; i < colStart + colSpan; i++) {
            _grid[row][i] = Grid.emptyItem(1, i);
        }
        this.setGrid(_grid);
    }

    getItemCoordinates(itemId: string, virtualized: boolean = true): ICoordinates | null {
        for (let i = 0; i < this.grid.length; i++) {
            const row = virtualized ? Grid.virtualizeRow(this.grid[i]) : this.grid[i];
            for (let j = 0; j < row.length; j++) {
                if (row[j].id === itemId) {
                    return { row: i, col: j, colSpan: row[j].colSpan ?? 1 };
                }
            }
        }
        return null;
    }

    getItem(itemId: string, virtualized: boolean = true): IGridItem | null {
        for (let i = 0; i < this.grid.length; i++) {
            const row = virtualized ? Grid.virtualizeRow(this.grid[i]) : this.grid[i];
            for (let j = 0; j < row.length; j++) {
                if (row[j].id === itemId) {
                    return row[j];
                }
            }
        }
        return null;
    }

    removeItem(coordinates: ICoordinates | null) {
        if (!coordinates) return;
        const _grid = cloneDeep(this.grid);
        const virtualRow = Grid.virtualizeRow(_grid[coordinates.row]);

        for (let i = coordinates.col; i < coordinates.col + (coordinates.colSpan ?? 1); i++) {
            virtualRow[i] = Grid.emptyItem(1, i);
        }

        _grid[coordinates.row] = Grid.devirtualizeRow(virtualRow);
        this.setGrid(_grid);
    }

    getRowConsecutiveSpace(row: IGridItem[], colSpan?: number): { hasSpace: boolean; consecutiveSpace: number; colStart: number } {
        if (!row) return { hasSpace: false, consecutiveSpace: 0, colStart: -1 };

        const virtualRow = Grid.virtualizeRow(row);
        let maxConsecutiveSpace = 0;
        let maxColStart = -1;
        let currentConsecutiveSpace = 0;
        let currentColStart = -1;

        for (let i = 0; i < this.dimensions.cols; i++) {
            const item = virtualRow[i];

            if (!item || item.empty) {
                // * Found empty space
                if (currentConsecutiveSpace === 0) {
                    currentColStart = i; // Start of new consecutive space
                }
                currentConsecutiveSpace++;

                //* If we have enough space for the requested colSpan, return immediately
                if (colSpan && currentConsecutiveSpace >= colSpan) {
                    return { hasSpace: true, consecutiveSpace: currentConsecutiveSpace, colStart: currentColStart };
                }
            } else {
                // *  Hit a non-empty item, record the space block if it's the largest so far
                if (currentConsecutiveSpace > maxConsecutiveSpace) {
                    maxConsecutiveSpace = currentConsecutiveSpace;
                    maxColStart = currentColStart;
                }
                // * Reset for next space block
                currentConsecutiveSpace = 0;
                currentColStart = -1;
            }
        }

        // * Check final space block (if row ends with empty spaces)
        if (currentConsecutiveSpace > maxConsecutiveSpace) {
            maxConsecutiveSpace = currentConsecutiveSpace;
            maxColStart = currentColStart;
        }

        return {
            hasSpace: colSpan ? maxConsecutiveSpace >= colSpan : maxConsecutiveSpace > 0,
            consecutiveSpace: maxConsecutiveSpace,
            colStart: maxColStart,
        };
    }

    static emptyItem(colSpan: number | undefined, colStart: number | undefined) {
        return {
            empty: true,
            id: StringUtils.uuidv4(),
            data: null,
            colSpan,
            colStart,
        } as IGridItem;
    }

    static virtualizeRow(row: IGridItem[]): IGridItem[] {
        if (!row) return [];
        const virtualRow: IGridItem[] = [];

        row.forEach((item) => {
            const colSpan = item.colSpan ?? 1;
            // Add the item colSpan times
            for (let i = 0; i < colSpan; i++) {
                virtualRow.push(item);
            }
        });

        return virtualRow;
    }

    static devirtualizeRow(virtualRow: IGridItem[]): IGridItem[] {
        const seen = new Set<string>();
        return virtualRow.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });
    }
    static fillEmptySpaces(row: IGridItem[]): IGridItem[] {
        const newRow: IGridItem[] = [];
        for (let i = 0; i < row.length; i++) {
            newRow[i] = row[i] ?? Grid.emptyItem(1, i);
        }
        return newRow;
    }

    getRowConsecutiveSpaceExcludingItems(
        row: IGridItem[],
        colSpan?: number,
        excludeItems?: string[]
    ): { hasSpace: boolean; consecutiveSpace: number; colStart: number } {
        const virtualRow = Grid.virtualizeRow(row);
        const virtualRowWithoutItems = virtualRow.filter((item) => !excludeItems?.includes(item.id));
        const virtualRowWithoutItemsDevirtualized = Grid.devirtualizeRow(virtualRowWithoutItems);

        return this.getRowConsecutiveSpace(virtualRowWithoutItemsDevirtualized, colSpan);
    }
}

export { Grid };
